<template>
  <div>
    <v-sheet elevation="1">
      <v-toolbar dense color="white">
        <v-toolbar-title>{{ screenTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>
      <v-container fluid>
        <error-message :error-object="errorObject" />
        <success-message :success-object="successObject" />
        <p style="margin: 5px 0 15px 0;">
          避難退域時検査場所状況は下の表を参照してください。条件を指定することで一覧から絞り込むことができます。<br />
          最新の情報が最大2,000件表示されます。
        </p>
        <v-alert dense outlined type="error" v-if="errorCheck">
          {{ errorCheck }}
        </v-alert>
        <datetime-picker
          :title="'検査日時(開始)'"
          :datetime="insDateStartFilterValue"
          :selectedDatetime.sync="insDateStartFilterValue"
        />
        <datetime-picker
          :title="'検査日時(終了)'"
          :datetime="insDateEndFilterValue"
          :selectedDatetime.sync="insDateEndFilterValue"
          :is-to="true"
          :is-search="true"
        />
        <v-row>
          <v-col cols="6" md="3">
            <v-template>
              <v-text-field
                v-model="insSiteNameFilterValue"
                label="検査場名"
                placeholder="部分一致絞り込み"
                append-icon="mdi-magnify"
                counter="50"
                maxlength="50"
                outlined
                clearable
                v-on="on"
                @change="setTableItem(false)"
              >
              </v-text-field>
            </v-template>
          </v-col>
          <v-col cols="6" md="3">
            <v-template>
              <v-text-field
                v-model="carNumberFilterValue"
                label="車番"
                placeholder="部分一致絞り込み"
                append-icon="mdi-magnify"
                counter="30"
                maxlength="30"
                outlined
                clearable
                v-on="on"
                @change="setTableItem(false)"
              >
              </v-text-field>
            </v-template>
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="insStatusFilterValue"
              :items="[
                {
                  value: 'all',
                  text: '全て'
                },
                ...insStatusItems
              ]"
              label="検査状況"
              placeholder="選択してください"
              item-text="text"
              item-value="value"
              clearable
              outlined
              @change="setTableItem(false)"
            >
            </v-select>
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="issStatusFilterValue"
              :items="[
                {
                  value: 'all',
                  text: '全て'
                },
                ...issStatusItems
              ]"
              label="通過証発行状況"
              placeholder="選択してください"
              item-text="text"
              item-value="value"
              clearable
              outlined
              @change="setTableItem(false)"
            >
            </v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn color="primary" v-on:click="downloadCSV">ダウンロード</v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <top-operation-data-table
              disp-mode="nowrap"
              :headers="headers"
              :items="items"
              :sort-by="['ins_date', 'ins_site_name', 'lane_number']"
              :sort-desc="[true, false, false]"
              class="fixedWidth"
              locale="ja-jp"
              must-sort
              ref="ref"
            >
            </top-operation-data-table>
          </v-col>
        </v-row>
      </v-container>
      <v-overlay
        :value="overlay"
        opacity="1"
        color="white"
        z-index="2147483647"
      >
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-sheet>
  </div>
</template>

<script>
import Vue from "vue";
import FormUtils from "@/plugins/form-utils";
import { required } from "vee-validate/dist/rules";
import { ScreenId } from "@/js/value-objects/screen-id";
import { extend, setInteractionMode } from "vee-validate";
import { fileDownload } from "@/js/utils";
import messageUtils from "@/mixins/message-utils";
setInteractionMode("eager");
extend("required", {
  ...required,
  message: "必須項目です。"
});
Vue.use(FormUtils);

export default {
  components: {
    DatetimePicker: () => import("@/components/DatetimePicker"),
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage"),
    TopOperationDataTable: () => import("@/components/ui/TopOperationDataTable")
  },

  mixins: [messageUtils],

  data: () => ({
    overlay: false, // オーバーレイ表示用
    items: [], // 避難退域時検査場所状況情報（データテーブル表示用）
    ignoreStartChage: false,
    ignoreEndChage: false,

    insDateStartFilterValue: "",
    insDateEndFilterValue: "",
    insSiteNameFilterValue: "",
    carNumberFilterValue: "",
    insStatusFilterValue: "",
    issStatusFilterValue: "",
    errorLimitOverFlag: false, // エラーフラグ（true：検索結果が2000件を超えた場合）
    screenId: ScreenId.SF1904, // 画面ID（避難退域時検査場所状況表示）
    screenTitle: ""
  }),

  computed: {
    /**
     * データテーブルのヘッダー定義
     */
    headers() {
      return [
        {
          text: "検査日時",
          align: "start",
          value: "ins_date"
        },
        {
          text: "検査場名",
          value: "ins_site_name"
        },
        {
          text: "レーン番号",
          value: "lane_number"
        },
        {
          text: "車番",
          value: "car_number"
        },
        {
          text: "乗車人数",
          value: "passengers"
        },
        {
          text: "検査状況",
          value: "ins_status"
        },
        {
          text: "通過証発行状況",
          value: "iss_status"
        },
        {
          text: "通過証発行日時",
          value: "iss_date"
        },
        {
          text: "通過証番号",
          value: "iss_number"
        }
      ];
    },

    insStatusItems() {
      return [
        {
          value: 0,
          text: "不合格"
        },
        {
          value: 1,
          text: "合格"
        }
      ];
    },

    issStatusItems() {
      return [
        {
          value: 0,
          text: "未発行"
        },
        {
          value: 1,
          text: "発行済"
        }
      ];
    },

    /**
     * フォームのプロパティ制御
     */
    formControlProps: () => {
      return function(counter = null) {
        return this.$formControlProps(this.editedItem.read_only, counter);
      };
    },

    /**
     * エラーチェック（車両検査結果情報一覧）
     */
    errorCheck() {
      let insDateStart, insDateEnd; // YYYY-MM-DD HH:mm形式（元データはsecondの情報も持っているが、判断基準にしない為省いている）
      if (this.insDateStartFilterValue) {
        insDateStart = this.$moment(
          this.insDateStartFilterValue,
          "YYYY-MM-DD HH:mm"
        );
      }
      if (this.insDateEndFilterValue) {
        insDateEnd = this.$moment(
          this.insDateEndFilterValue,
          "YYYY-MM-DD HH:mm"
        );
      }
      /* 検査日時の前後チェック */
      if (insDateStart && insDateEnd) {
        if (insDateEnd.isBefore(insDateStart)) {
          return "終了検査日時が開始検査日時より前の日時です。";
        }
      }
      return "";
    }
  },

  watch: {
    insDateStartFilterValue() {
      if (this.ignoreStartChange) {
        this.ignoreStartChange = false;
        return;
      }

      this.setTableItem(false);
    },
    insDateEndFilterValue() {
      if (this.ignoreEndChange) {
        this.ignoreEndChange = false;
        return;
      }

      this.setTableItem(false);
    },
    $route: "initialize"
  },

  created() {
    this.initialize();
  },

  methods: {
    /**
     * CSVダウンロード処理
     */
    downloadCSV() {
      let csv = "\ufeff";

      /* タイムスタンプ算出 */
      let now = new Date();
      let year = now.getFullYear();
      let month = ("00" + (now.getMonth() + 1)).slice(-2);
      let day = ("00" + now.getDate()).slice(-2);
      let hour = ("00" + now.getHours()).slice(-2);
      let minute = ("00" + now.getMinutes()).slice(-2);
      let timeStamp = year + month + day + "_" + hour + minute;

      /* ヘッダー */
      const headers = {
        ins_date: "検査日時",
        ins_site_name: "検査場名",
        lane_number: "レーン番号",
        car_number: "車番",
        passengers: "乗車人数",
        ins_status: "検査状況",
        iss_status: "通過証状況",
        iss_date: "通過証発行日時",
        iss_number: "通過証番号"
      };
      const headerList = [];
      Object.keys(headers).forEach(key => {
        headerList.push(headers[key]);
      });
      csv += headerList.join(",") + "\n";

      /* Body（本文） */
      let sorted = JSON.parse(JSON.stringify(this.items));
      sorted = this.$refs.ref.$children[0].$children[0].sortItems(sorted);
      sorted.forEach(item => {
        const itemList = [];
        Object.keys(headers).forEach(header => {
          if (item[header] !== undefined) {
            itemList.push(item[header]);
          }
        });
        if (itemList.length > 0) {
          csv += itemList.join(",") + "\n";
        }
      });
      let blob = new Blob([csv], { type: "text/csv" });
      fileDownload(blob, "検査結果一覧_" + timeStamp + ".csv");
    },

    /**
     * 初期化処理
     */
    async initialize() {
      this.overlay = true;

      const screen = this.$screenOperationAuthority(ScreenId.SF1904);
      this.screenId = screen.screenId;
      this.screenTitle = screen.screenTitle;

      this.ignoreStartChange = true;
      this.ignoreEndChange = true;

      const nowDate = this.$moment().format("YYYY/MM/DD");
      this.insDateStartFilterValue = nowDate + " 00:00:00";
      this.insDateEndFilterValue = nowDate + " 23:59:00";

      /* テーブルアイテムを設定 */
      await this.setTableItem();

      this.overlay = false;
    },

    /**
     * テーブルアイテム設定
     * @param {*} limitFlag 件数制限フラグ（2000件を超える場合にエラーレスポンスを返却したい場合にfalseを設定）
     */
    async setTableItem(limitFlag = true) {
      // 検索項目にエラーがある場合は、検索を実行しない
      if (this.errorCheck) {
        return;
      }

      this.clearMessage();
      this.overlay = true;

      /* 車両検査結果情報 */
      let payload = {
        limit_flg: limitFlag,
        screenId: this.screenId
      };
      // 検査日時（開始）
      if (this.insDateStartFilterValue) {
        payload.ins_date_start = this.$moment(
          this.insDateStartFilterValue
        ).format("YYYY/MM/DD HH:mm");
      }
      // 検査日時（終了）
      if (this.insDateEndFilterValue) {
        payload.ins_date_end = this.$moment(this.insDateEndFilterValue).format(
          "YYYY/MM/DD HH:mm"
        );
      }
      // 検査場名
      if (this.insSiteNameFilterValue) {
        payload.ins_site_name = this.insSiteNameFilterValue;
      }
      // 車番
      if (this.carNumberFilterValue) {
        payload.car_number = this.carNumberFilterValue;
      }
      // 検査状況
      if (
        this.isNotEmptyNumber(this.insStatusFilterValue) &&
        this.insStatusFilterValue !== "all"
      ) {
        payload.ins_status = this.insStatusFilterValue;
      }
      // 通過証発行状況
      if (
        this.isNotEmptyNumber(this.issStatusFilterValue) &&
        this.issStatusFilterValue !== "all"
      ) {
        payload.iss_status = this.issStatusFilterValue;
      }
      //車両検査結果情報取得
      await this.$store
        .dispatch("inspectionResult/loadInspectionResult", payload)
        .then(() => {
          this.items = this.convertItems(
            this.$store.state.inspectionResult.data.results
          );
        })
        .catch(e => {
          if (e.response.data.error.code == "ERAE40007") {
            // 検索結果が2000件を超えた場合
            this.handleGetError(e);
          } else {
            this.handleError(e, this.screenId, "aggregation-menu");
          }
        });

      this.overlay = false;
    },

    /**
     * 数値未入力判定
     */
    isNotEmptyNumber(value) {
      if (
        value === "" ||
        value === undefined ||
        value === null ||
        isNaN(value)
      ) {
        return false;
      }
      return true;
    },

    /**
     * データテーブル表示用
     */
    formatDatetime(datetime) {
      const formattedDatetime = this.$moment(datetime);
      if (formattedDatetime.isValid()) {
        return formattedDatetime.format("YYYY/MM/DD HH:mm");
      }
      return "";
    },

    /**
     * データテーブル表示用（検査状況）
     */
    inspectionStatus(item) {
      const data = this.insStatusItems.find(obj => obj.id === item.ins_status);
      if (data) {
        return data.name;
      }
      return "－";
    },

    /**
     * データテーブル表示用（通過証発行状況）
     */
    issuanceStatus(item) {
      const data = this.issStatusItems.find(obj => obj.id === item.iss_status);
      if (data) {
        return data.name;
      }
      return "－";
    },

    convertItems(sourceItems) {
      var items = [];

      var convertText = function(target, sourceList) {
        var filtered = sourceList.filter(function(source) {
          return target == source.value;
        });
        if (filtered.length !== 1) {
          return "";
        }
        return filtered[0].text;
      };

      var insStatusItems = this.insStatusItems;
      var issStatusItems = this.issStatusItems;

      var self = this;
      sourceItems.forEach(function(item) {
        item.passengers = item.passengers ? item.passengers : "";
        item.iss_date = item.iss_date ? item.iss_date : "";
        item.iss_number = item.iss_number ? item.iss_number : "";
        item.ins_status = convertText(item.ins_status, insStatusItems);
        item.iss_status = convertText(item.iss_status, issStatusItems);
        item.ins_date = self.formatDatetime(item.ins_date);
        item.iss_date = self.formatDatetime(item.iss_date);

        items.push(item);
      });
      return items;
    }
  }
};
</script>
<style scoped>
.list-file-name {
  color: black;
}
</style>
